import { getSiteApi } from '@/api-calls/sites/get'
import { updateSiteApi } from '@/api-calls/sites/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@blogging/ui'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

const AVAILABLE_FONTS = [
  { key: 'system', name: 'System', category: 'system' },
  { key: 'inter', name: 'Inter', category: 'sans-serif' },
  { key: 'geistSans', name: 'Geist Sans', category: 'sans-serif' },
  { key: 'roboto', name: 'Roboto', category: 'sans-serif' },
  { key: 'openSans', name: 'Open Sans', category: 'sans-serif' },
  { key: 'notoSans', name: 'Noto Sans', category: 'sans-serif' },
  { key: 'dmSans', name: 'DM Sans', category: 'sans-serif' },
  { key: 'plusJakartaSans', name: 'Plus Jakarta Sans', category: 'sans-serif' },
  { key: 'spaceGrotesk', name: 'Space Grotesk', category: 'sans-serif' },
  { key: 'outfit', name: 'Outfit', category: 'sans-serif' },
  { key: 'nunito', name: 'Nunito', category: 'sans-serif' },
  { key: 'poppins', name: 'Poppins', category: 'sans-serif' },
  { key: 'raleway', name: 'Raleway', category: 'sans-serif' },
  { key: 'sourceSans', name: 'Source Sans 3', category: 'sans-serif' },
  { key: 'workSans', name: 'Work Sans', category: 'sans-serif' },
  { key: 'lora', name: 'Lora', category: 'serif' },
  { key: 'merriweather', name: 'Merriweather', category: 'serif' },
  { key: 'playfairDisplay', name: 'Playfair Display', category: 'serif' },
  { key: 'geistMono', name: 'Geist Mono', category: 'monospace' },
] as const

function FontSelector({
  label,
  description,
  value,
  onChange,
}: {
  label: string
  description: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-base font-medium">{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select a font" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default" className="font-sans">
            Theme Default
          </SelectItem>
          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
            Sans-serif
          </div>
          {AVAILABLE_FONTS.filter((f) => f.category === 'sans-serif').map(
            (fontOption) => (
              <SelectItem key={fontOption.key} value={fontOption.key}>
                {fontOption.name}
              </SelectItem>
            ),
          )}
          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
            Serif
          </div>
          {AVAILABLE_FONTS.filter((f) => f.category === 'serif').map(
            (fontOption) => (
              <SelectItem key={fontOption.key} value={fontOption.key}>
                {fontOption.name}
              </SelectItem>
            ),
          )}
          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
            Monospace
          </div>
          {AVAILABLE_FONTS.filter((f) => f.category === 'monospace').map(
            (fontOption) => (
              <SelectItem key={fontOption.key} value={fontOption.key}>
                {fontOption.name}
              </SelectItem>
            ),
          )}
        </SelectContent>
      </Select>
    </div>
  )
}

export function SiteSettingsThemePage() {
  const { siteId } = useParams()
  const queryClient = useQueryClient()
  const [darkMode, setDarkMode] = useState<string | null>(null)
  const [fontHeader, setFontHeader] = useState<string | null>(null)
  const [fontBody, setFontBody] = useState<string | null>(null)

  const {
    data: site,
    isLoading,
    error,
  } = useFetch(getSiteApi, {
    resourceName: 'site',
    initialParams: { id: siteId as string },
  })

  const { mutate: updateSite, isPending: isUpdating } = useMutation(
    updateSiteApi,
    {
      resourceName: 'site',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getSiteApi.apiName, { id: siteId }],
        })
        toast.success('Theme settings updated successfully!')
        setDarkMode(null)
        setFontHeader(null)
        setFontBody(null)
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update theme settings')
      },
    },
  )

  const handleSave = () => {
    if (darkMode === null && fontHeader === null && fontBody === null) return
    updateSite({
      id: siteId as string,
      ...(darkMode !== null && { darkMode }),
      ...(fontHeader !== null && { fontHeader }),
      ...(fontBody !== null && { fontBody }),
    })
  }

  const hasChanges = darkMode !== null || fontHeader !== null || fontBody !== null

  if (isLoading) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <Skeleton className="h-48 w-full rounded-lg" />
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  if (error) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <div className="text-destructive">Failed to load site data</div>
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">Theme</h1>
          <p className="text-muted-foreground">
            Configure dark mode and typography for your site.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-8">
          <h3 className="text-lg font-semibold">Theme Settings</h3>

          <div className="space-y-3">
            <div>
              <Label className="text-base font-medium">Dark Mode</Label>
              <p className="text-sm text-muted-foreground">
                Enable dark mode support for your site visitors.
              </p>
            </div>

            <Select
              value={darkMode ?? site?.darkMode ?? 'default'}
              onValueChange={(value) => setDarkMode(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select dark mode option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  Light Only (No dark mode)
                </SelectItem>
                <SelectItem value="dark_mode">
                  Dark Mode Enabled (with theme switcher)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <FontSelector
            label="Header Font"
            description="Choose the font for headings and titles (h1, h2, h3, etc.)."
            value={fontHeader ?? site?.fontHeader ?? 'system'}
            onChange={(value) => setFontHeader(value)}
          />

          <FontSelector
            label="Body Font"
            description="Choose the font for body text and paragraphs."
            value={fontBody ?? site?.fontBody ?? 'system'}
            onChange={(value) => setFontBody(value)}
          />

          <div className="flex flex-col items-start space-y-4 rounded-lg border bg-muted/50 p-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex-1">
              <p className="text-sm font-medium">Save theme settings</p>
              <p className="text-sm text-muted-foreground">
                Update your site's theme configuration.
              </p>
            </div>
            <Button
              onClick={handleSave}
              disabled={isUpdating || !hasChanges}
              className="w-full sm:w-auto"
            >
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUpdating ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
